import { useMemo, useEffect, useRef } from 'react';
import { Conditional } from '@consumidor-positivo/aurora';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import PageLoader from '@common/components/PageLoader';
import constants from '@common/constants';
import { DebtsProvider } from '@common/contexts/DebtsContext/DebtsProvider';
import useAuth from '@common/hooks/useAuth';
import { useUser } from '@common/hooks/useUser';
import usePushNotification from '@common/hooks/usePushNotification';
import { useWebBridgeContext } from '@libs/app-bridge';
import PrivatePageTemplate from '@common/templates/PrivatePage';
import useEngageEmailStatus from '@libs/acp/hooks/useEngageEmailStatus';

import { useRedline } from '@libs/redline';
import { getCookie } from '@libs/utils/helpers/getSafeCookies';
import { getPathNamePermissions } from '@libs/acp/utils/getPathNameAccessPermissions';
import { JaiminhoClient } from '@libs/jaiminho';
import { pageNames } from '@common/routes/pageNames';

import useNavigationGuard from '@libs/acp/hooks/useNavigationGuard';

import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';
import { useAuthentication } from '@libs/acp/contexts/Authentication';

import { identityLoginEnrichment } from '@libs/acp/apis/signIn/signInAPI';

import '../styles.scss';

const allowedPathsForJaiminho = [pageNames.myDebts.path];

const isCustomBuild = !!import.meta.env.VITE_CUSTOM_BUILD;

export default function PrivateRoutes() {
  const hasTriggeredValidateCredentialsRedirect = useRef(false);

  const { document: userDocument, email: userEmail } = useUser();
  const { authenticatedUser, hasPermission, acpLogout, tokenOrigin } = useAuthenticatedUser();

  const { access_token } = authenticatedUser.auth || {};

  const ACPNavigationObserver = () => {
    useNavigationGuard();
    return null;
  };

  const { isLoading, isAuthenticated: isAuthenticatedACP, tokens } = useAuthentication();

  const jaiminhoToken = tokens?.accessToken || access_token || '';

  const isAtendeFlow = tokenOrigin() === 'atende' || !!getCookie('ATENDE_SIGNUP');

  const { getEmailStatus } = useEngageEmailStatus();

  const { logOut } = useAuth();

  const { track: trackPushNotification } = usePushNotification();
  const { notifications } = useWebBridgeContext();

  const { track: trackRedline } = useRedline();

  useEffect(() => {
    notifications.applyPushOnEngage(userEmail, isAuthenticatedACP);
  }, [userEmail, isAuthenticatedACP]);

  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    if (isAuthenticatedACP) {
      trackPushNotification({ documento: userDocument });

      if (!isAtendeFlow) {
        identityLoginEnrichment().catch(async (err) => {
          if (err.error_slug === 'customer_not_found') {
            const event = new CustomEvent('logout_trail', {
              detail: {
                trigger: 'login_enrichment_customer_not_found_provider',
              },
            });
            window.dispatchEvent(event);

            logOut();
          }
        });

        getEmailStatus();
      }

      trackRedline.userAuthentication.userIdentified({
        document: userDocument,
      });
    }
  }, [isAuthenticatedACP]);

  const canAccess = useMemo(() => {
    if (hasPermission('all')) return true;

    const currentPathPermissions = getPathNamePermissions(pageNames, pathname);

    const userPermissions = [];
    currentPathPermissions?.forEach((permission) => {
      if (hasPermission(permission)) userPermissions.push(permission);
    });

    return userPermissions.length > 0;
  }, [pathname]);

  const shouldExecuteJaiminho = allowedPathsForJaiminho.some((path) => window.location.pathname === path) && !isAtendeFlow;

  if (isLoading) {
    return <PageLoader />;
  }

  const redirectToSignInWithAuthProvider = !isAuthenticatedACP && !isLoading;
  if (redirectToSignInWithAuthProvider) {
    if (isCustomBuild) {
      window.location.href = `${pageNames.signin.path}${window.location.search}`;
      return null;
    }
    return <Navigate to={{ pathname: pageNames.signin.path, search: `${window.location.search}` }} replace />;
  }

  const mustValidateCredentials =
    !!isAuthenticatedACP && !pathname.includes(pageNames.myDebts.path) && !canAccess && !hasTriggeredValidateCredentialsRedirect.current;
  if (mustValidateCredentials) {
    hasTriggeredValidateCredentialsRedirect.current = true;
    return (
      <Navigate
        to={{ pathname: pageNames.unlockProtectedFeature.path }}
        replace
        state={{
          featureName:
            constants.unlockFeatureTag[pathname?.split('?')[0] as keyof typeof constants.unlockFeatureTag] || 'DESBLOQUEIE A FUNCIONALIDADE',
          featureSlug: pathname,
        }}
      />
    );
  } else {
    hasTriggeredValidateCredentialsRedirect.current = false;
  }

  return (
    <Conditional
      condition={!!isAuthenticatedACP}
      renderIf={
        <DebtsProvider>
          <PrivatePageTemplate>
            <ACPNavigationObserver />
            <Outlet />
            {shouldExecuteJaiminho && (
              <JaiminhoClient
                apiKey={import.meta.env.VITE_PUSH_WEB_API_KEY}
                marketplaceApi={`${import.meta.env.VITE_ACP_IDENTITY}/v2/app/engage/create-entity`}
                urgencyInstall={false}
                applicationServerKey={import.meta.env.VITE_PUSH_WEB_SERVER_KEY}
                token={jaiminhoToken}
                options={{
                  pushOnTimeout: 15000,
                  promptTitle: '',
                  promptMessage: 'Olá, você gostaria de ser avisado sobre novas ofertas?',
                  promptButton: 'Sim',
                  promptDismiss: 'Não',
                  bell: false,
                  button: false,
                  prompt: true,
                }}
              />
            )}
          </PrivatePageTemplate>
        </DebtsProvider>
      }
    />
  );
}
